import { useEffect, useState } from "react";
import { api, formatPrice } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import AdminNav, { AdminHeader } from "@/components/AdminNav";

const STATE_STYLE = {
  payment_pending: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
  payment_confirmed: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  supplier_selected: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  forwarded_to_supplier: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  supplier_accepted: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
  shipped: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  tracking_received: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  delivered: "bg-green-500/10 text-green-400 border-green-500/30",
  exception_human_review: "bg-red-500/10 text-red-400 border-red-500/30",
  cancelled: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
  refunded: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
};

const STATES = [
  "payment_pending","payment_confirmed","supplier_selection_pending","supplier_selected",
  "awaiting_supplier_confirmation","forwarded_to_supplier","supplier_accepted","supplier_rejected",
  "backorder","shipped","tracking_received","delivered","return_requested","return_authorized",
  "returned","refunded","exception_human_review","cancelled",
];

export default function Admin() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/admin/orders").then(({ data }) => setOrders(data.orders)).finally(() => setLoading(false));
  useEffect(load, []);

  const setState = async (id, state) => {
    try {
      await api.post(`/admin/orders/${id}/state`, { state });
      toast.success("State updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  return (
    <div className="container-tight py-8" data-testid="admin-page">
      <AdminNav />
      <AdminHeader title="ORDER MANAGEMENT" subtitle="Every order across the expanded lifecycle." />

      {loading ? <div className="font-mono text-zinc-500">LOADING...</div> : (
        <div className="border border-zinc-800 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#050505] border-b border-zinc-800 font-mono text-[10px] tracking-widest text-zinc-500 uppercase">
                <th className="p-3 text-left">Order</th>
                <th className="p-3 text-left">Customer</th>
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-right">Total</th>
                <th className="p-3 text-right">Est. Profit</th>
                <th className="p-3 text-left">Payment</th>
                <th className="p-3 text-left">State</th>
                <th className="p-3 text-left">Set State</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const state = o.state || o.status;
                return (
                  <tr key={o.id} className="border-b border-zinc-900 hover:bg-[#0a0a0c]" data-testid={`admin-order-${o.id}`}>
                    <td className="p-3 font-mono text-xs">{o.id.slice(0, 8).toUpperCase()}</td>
                    <td className="p-3 text-zinc-300">{o.user_email}</td>
                    <td className="p-3 font-mono text-xs text-zinc-500">{new Date(o.created_at).toLocaleDateString()}</td>
                    <td className="p-3 font-mono font-bold text-right">{formatPrice(o.total)}</td>
                    <td className="p-3 font-mono text-right text-green-400">{o.contribution_profit_estimate != null ? formatPrice(o.contribution_profit_estimate) : "—"}</td>
                    <td className="p-3 font-mono text-xs">{o.payment_status}</td>
                    <td className="p-3">
                      <Badge variant="outline" className={`rounded-sm font-mono text-[10px] ${STATE_STYLE[state] || ""}`}>
                        {state}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <Select value={state} onValueChange={(v) => setState(o.id, v)}>
                        <SelectTrigger data-testid={`state-select-${o.id}`} className="w-56 bg-[#121217] border-zinc-800 rounded-sm h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a0a0c] border-zinc-800 max-h-72">
                          {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                );
              })}
              {orders.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-zinc-500 font-mono">NO ORDERS</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
